import React from "react";
import { ClipboardList } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { DD_MM_YYYY, timeFormat } from "src/helpers";
import { StatusPill } from "src/components/ui";
import { WarehouseCheckInStatus, WarehouseCheckInType } from "src/types";
import { useAuthorisation } from "src/context";

interface WarehouseCheckInHeadingSectionProps {
  type?: WarehouseCheckInType;
  warehouseCheckInId: string;
  status?: WarehouseCheckInStatus;
  timestamp?: string;
}
const WarehouseCheckInHeadingSection: React.FC<WarehouseCheckInHeadingSectionProps> = ({
  type,
  warehouseCheckInId,
  status,
  timestamp
}) => {
  const { userRole } = useAuthorisation();
  let statusLinkPath: string | undefined;

  if (status === "Flagged" && userRole !== "WAREHOUSE") {
    statusLinkPath =
      type === "PURCHASE_ORDER"
        ? `${ROUTES_CONFIG.receivingDiscrepancies.path}?id=${warehouseCheckInId}&timestamp=${timestamp}`
        : `${ROUTES_CONFIG.unknownArrivals.path}?id=${warehouseCheckInId}&timestamp=${timestamp}`;
  }

  const formattedTimestamp =
    timestamp && `${DD_MM_YYYY.formatDate(new Date(timestamp))} · ${timeFormat(new Date(timestamp))}`;
  const checkInTypeLabel =
    type === "PURCHASE_ORDER"
      ? "Purchase Order Check-In"
      : type === "UNKNOWN_ARRIVAL"
        ? "Unknown Arrival"
        : "Warehouse Check-In";

  return (
    <div>
      <section className="overflow-hidden rounded-2xl border border-secondary-200 shadow-soft bg-white">
        <div className="bg-gradient-to-r from-secondary-900 via-secondary-800 to-secondary-700 px-8 py-6 sm:px-10 sm:py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-white/10 p-3 text-white">
                <ClipboardList className="h-7 w-7" />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-200">
                  Warehouse Check-In
                </p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">{warehouseCheckInId}</h1>
                <p className="mt-3 text-sm text-secondary-100">
                  Review product check-ins, operations updates, and warehouse records for this intake.
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {checkInTypeLabel && (
                    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-secondary-100">
                      {checkInTypeLabel}
                    </span>
                  )}
                  {formattedTimestamp && (
                    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-secondary-100">
                      {formattedTimestamp}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {status && (
              <div className="flex items-center justify-end">
                <StatusPill status={status} linkPath={statusLinkPath} />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export { WarehouseCheckInHeadingSection };
